import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { galleryApi } from '../services/api';
import '../styles/DiaDeCampoCallout.css';

const DDC_GALLERY_ID = 8;

const stats = [
  { value: 3200, suffix: '+', label: 'Pessoas impactadas' },
  { value: 80,   suffix: '+', label: 'Empresas participantes' },
  { value: 8,    suffix: 'ª', label: 'Edições realizadas' },
];

function useCountUp(target: number, duration = 1800, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

const StatItem: React.FC<{ value: number; suffix: string; label: string; active: boolean }> = ({
  value, suffix, label, active,
}) => {
  const count = useCountUp(value, 1600, active);
  return (
    <div className="callout-stat">
      <span className="callout-stat-number">
        {count.toLocaleString('pt-BR')}{suffix}
      </span>
      <span className="callout-stat-label">{label}</span>
    </div>
  );
};

const DiaDeCampoCallout: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [slides, setSlides] = useState<{ src: string; alt: string }[]>([]);
  const [statsActive, setStatsActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    galleryApi.getById(DDC_GALLERY_ID).then(galeria => {
      const fotos = [...galeria.fotos].sort((a, b) => a.ordem - b.ordem);
      setSlides(fotos.map(f => ({ src: f.url, alt: f.legenda || 'Dia de Campo Agricampanha' })));
    }).catch(() => {});
  }, []);
  const statsRef = useRef<HTMLDivElement>(null);

  const startTimer = useCallback((len: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (len <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % len);
    }, 3500);
  }, []);

  useEffect(() => {
    startTimer(slides.length);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [slides.length, startTimer]);

  const goTo = useCallback((index: number, len: number) => {
    setCurrent(index);
    startTimer(len);
  }, [startTimer]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsActive(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="dia-de-campo-callout">
      {/* Fita grudada no topo */}
      <div
        className="callout-fita"
        style={{ backgroundImage: "url('/img/bg-fita.png')" }}
      />

      {/* Conteúdo acima dos stats */}
      <div className="callout-inner">
        <img
          src="/img/dia-de-campo/logo-dia-de-campo.png"
          alt="8º Dia de Campo Agricampanha"
          className="callout-logo"
        />
        <p className="callout-slogan">
          Um encontro que une produtores, conhecimento,<span className="callout-slogan-br"><br /></span>{' '}e as melhores práticas do campo gaúcho.
        </p>
        <div className="callout-info">
          <span><i className="bi bi-calendar-event"></i> 05 de março de 2026</span>
          <span><i className="bi bi-geo-alt"></i> Agropecuária San Diego — Bagé/RS</span>
        </div>
      </div>

      {/* Contador de impacto */}
      <div className="callout-stats" ref={statsRef}>
        {stats.map((s) => (
          <StatItem key={s.label} value={s.value} suffix={s.suffix} label={s.label} active={statsActive} />
        ))}
      </div>

      {/* Faixa verde escura com slider */}
      <div className="callout-slider-band">
        <div className="callout-slider">
          {slides.map((slide, i) => (
            <img
              key={i}
              src={slide.src}
              alt={slide.alt}
              className={`callout-slide${i === current ? ' active' : ''}`}
            />
          ))}
          {slides.length > 1 && (
            <>
              <button className="callout-arrow callout-arrow--prev" onClick={() => goTo((current - 1 + slides.length) % slides.length, slides.length)} aria-label="Anterior">
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="callout-arrow callout-arrow--next" onClick={() => goTo((current + 1) % slides.length, slides.length)} aria-label="Próximo">
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}
          <div className="callout-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`callout-dot${i === current ? ' active' : ''}`}
                onClick={() => goTo(i, slides.length)}
                aria-label={`Foto ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Botão abaixo da faixa */}
      <div className="callout-inner">
        <Button
          variant="primary"
          onClick={() => navigate('/dia-de-campo')}
          className="callout-btn"
        >
          Saiba como foi o evento <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    </section>
  );
};

export default DiaDeCampoCallout;
